import {
  JSNS,
  type Folder,
  type Message,
  type Conversation,
  type Tag,
  type SearchFolder,
  type JsonObject,
  type MailSearchType,
  type SortBy,
  type SearchCursor,
  type SearchResponse,
  type AutoCompleteMatch,
  type SpellCheckResult,
  type MisspelledWord,
  type MailboxMetadata,
  type MetadataValue,
  type BrowseData,
  type BrowseBy,
  type RetentionPolicy,
  type FolderView,
  type Grant,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

/** Item types a SearchRequest can return (comma-joined on the wire). */
export type SearchType = MailSearchType;

export interface SearchParams {
  /** Zimbra query string, e.g. "in:inbox is:unread", "inid:2", "from:bob". */
  query: string;
  /** one or more types (default: message) */
  types?: SearchType | SearchType[];
  /** sort order, e.g. dateDesc | dateAsc | subjAsc | readDesc | sizeDesc … */
  sortBy?: SortBy | string;
  /** paging offset (0-based) */
  offset?: number;
  /** page size (default 10, server cap 1000) */
  limit?: number;
  /** cursor for deep paging (preferred over offset) */
  cursor?: SearchCursor;
  /** fetch the first matching hit's content inline (1|first|all|u|hits|<id>) */
  fetch?: string;
  /** include recipients (0|1|2) — for Sent view */
  recip?: 0 | 1 | 2;
  /** return the full conversation (0|1) */
  fullConversation?: boolean;
  /** mark inlined hits as read */
  read?: boolean;
  /** expand groups/contacts needed for display (0|1) */
  needExp?: boolean;
  /** neuter images in returned HTML (0|1) */
  neuter?: boolean;
  /** id vs full result mode */
  resultMode?: 'NORMAL' | 'IDS';
  /** default header field to search */
  field?: string;
  /** search the dumpster (recover-deleted) store */
  inDumpster?: boolean;
  /** expand recurring appointment instances between these epoch-ms bounds */
  calExpandInstStart?: number;
  calExpandInstEnd?: number;
  /** return HTML body when fetching */
  html?: boolean;
  /** locale / timezone hints */
  locale?: string;
  tz?: string;
}

export interface MessageActionOptions {
  /** target folder id (for move) */
  folderId?: string;
  /** tag name/id, flag list, color, etc. */
  [k: string]: unknown;
}

/** Ops accepted by MsgAction / ConvAction. */
export type MsgOp =
  | 'read'
  | '!read'
  | 'flag'
  | '!flag'
  | 'spam'
  | '!spam'
  | 'trash'
  | 'delete'
  | 'move'
  | 'copy'
  | 'update'
  | 'tag'
  | '!tag'
  | 'mute'
  | '!mute';

/** Shared shape for the typed actions (Msg / Conv / Item). */
export interface ActionOptions {
  /** target folder id (`l`) for move/copy */
  folderId?: string;
  /** tag names (`tn`, preferred over deprecated numeric `t`) */
  tagNames?: string[];
  /** flag string (`f`) for `update` */
  flags?: string;
  /** color index (0-9) */
  color?: number;
  /** explicit rgb (#rrggbb) */
  rgb?: string;
  /** new name for `rename`/`update` */
  name?: string;
  /** conversation constraint (`tcon`): subset of t,j,s,d,o (optionally `-`) */
  constraints?: string;
}

/** Folder ops accepted by FolderAction. */
export type FolderOp =
  | 'move'
  | 'rename'
  | 'delete'
  | 'empty'
  | 'read'
  | 'color'
  | 'grant'
  | '!grant'
  | 'revokeorphangrants'
  | 'url'
  | 'import'
  | 'fb'
  | 'check'
  | '!check'
  | 'syncon'
  | '!syncon'
  | 'retentionpolicy'
  | 'disableactivesync'
  | 'update';

function joinIds(ids: string | string[]): string {
  return Array.isArray(ids) ? ids.join(',') : ids;
}

function csvToList(v: unknown): string[] {
  if (typeof v !== 'string' || v.length === 0) return [];
  return v.split(',');
}

/** Coerce a 0|1|boolean wire value to a definite boolean. */
function asBool(v: unknown): boolean {
  return v === 1 || v === '1' || v === true;
}

/**
 * Mail domain (urn:zimbraMail) — messages, conversations, folders, tags,
 * search & search-folders, item/conv/folder/tag actions, attachments,
 * spell-check, autocomplete, mailbox metadata, browse, notifications/sync.
 *
 * One typed method per SOAP command (see docs/features/mail.md). Compose
 * (SendMsg/SaveDraft/…) lives in ComposeApi and filters in FiltersApi; this
 * class owns everything else in the mail slice.
 *
 * NOTE: the original four methods (search, getMessage, getFolderTree,
 * messageAction) are preserved verbatim in name + signature — the smoke test
 * depends on them.
 */
export class MailApi {
  constructor(private readonly client: ZimbraClient) {}

  /* ============================================================== */
  /* Search                                                          */
  /* ============================================================== */

  /** SearchRequest. */
  async search(params: SearchParams): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'Search', this.buildSearchBody(params));
  }

  /** Build a SearchRequest body (shared by search + searchAction). */
  private buildSearchBody(params: SearchParams): JsonObject {
    const types = Array.isArray(params.types)
      ? params.types.join(',')
      : (params.types ?? 'message');
    const body: JsonObject = { query: params.query, types };
    if (params.sortBy) body.sortBy = params.sortBy;
    if (params.offset !== undefined) body.offset = params.offset;
    if (params.limit !== undefined) body.limit = params.limit;
    if (params.cursor) body.cursor = params.cursor;
    if (params.fetch) body.fetch = params.fetch;
    if (params.recip !== undefined) body.recip = params.recip;
    if (params.fullConversation) body.fullConversation = 1;
    if (params.read) body.read = 1;
    if (params.needExp) body.needExp = 1;
    if (params.neuter !== undefined) body.neuter = params.neuter ? 1 : 0;
    if (params.resultMode) body.resultMode = params.resultMode;
    if (params.field) body.field = params.field;
    if (params.inDumpster) body.inDumpster = 1;
    if (params.html) body.html = 1;
    if (params.locale) body.locale = { _content: params.locale };
    if (params.tz) body.tz = { id: params.tz };
    if (params.calExpandInstStart !== undefined)
      body.calExpandInstStart = params.calExpandInstStart;
    if (params.calExpandInstEnd !== undefined)
      body.calExpandInstEnd = params.calExpandInstEnd;
    return body;
  }

  /** SearchConvRequest — expand a conversation, scoped to a search constraint. */
  async searchConv(req: {
    cid: string;
    query?: string;
    fetch?: string;
    html?: boolean;
    recip?: 0 | 1 | 2;
    sortBy?: SortBy | string;
    offset?: number;
    limit?: number;
    read?: boolean;
    needExp?: boolean;
  }): Promise<SearchResponse> {
    const body: JsonObject = { cid: req.cid };
    if (req.query) body.query = req.query;
    if (req.fetch) body.fetch = req.fetch;
    if (req.html) body.html = 1;
    if (req.recip !== undefined) body.recip = req.recip;
    if (req.sortBy) body.sortBy = req.sortBy;
    if (req.offset !== undefined) body.offset = req.offset;
    if (req.limit !== undefined) body.limit = req.limit;
    if (req.read) body.read = 1;
    if (req.needExp) body.needExp = 1;
    return this.client.call<SearchResponse>(JSNS.mail, 'SearchConv', body);
  }

  /**
   * SearchActionRequest — run a bulk action over the ENTIRE result set of a
   * Search in one call (e.g. "select all 5,000 matches → mark read").
   *
   * ASSUMED - verify vs live capture: the wrapper element names (`<search>` +
   * `<bulkAction>`) are reconstructed from the command's purpose (mail.md open
   * question 5); confirm against the 8.7.11 SOAP ref before relying on it.
   */
  async searchAction(req: {
    search: SearchParams;
    bulkAction: { op: string; folderId?: string; constraints?: string };
  }): Promise<JsonObject> {
    const bulkAction: JsonObject = { op: req.bulkAction.op };
    if (req.bulkAction.folderId) bulkAction.l = req.bulkAction.folderId;
    if (req.bulkAction.constraints) bulkAction.tcon = req.bulkAction.constraints;
    return this.client.call(JSNS.mail, 'SearchAction', {
      search: this.buildSearchBody(req.search),
      bulkAction,
    });
  }

  /* ============================================================== */
  /* Read items                                                      */
  /* ============================================================== */

  /** GetMsgRequest — fetch a single message, optionally with HTML body expanded. */
  async getMessage(
    id: string,
    opts?: { html?: boolean; raw?: boolean },
  ): Promise<{ m?: Message[] }>;
  async getMessage(req: {
    id: string;
    read?: boolean;
    html?: boolean;
    raw?: boolean;
    neuter?: boolean;
    part?: string;
    max?: number;
    needExp?: boolean;
    ridZ?: string;
    headers?: string[];
  }): Promise<{ m?: Message[] }>;
  async getMessage(
    idOrReq:
      | string
      | {
          id: string;
          read?: boolean;
          html?: boolean;
          raw?: boolean;
          neuter?: boolean;
          part?: string;
          max?: number;
          needExp?: boolean;
          ridZ?: string;
          headers?: string[];
        },
    opts?: { html?: boolean; raw?: boolean },
  ): Promise<{ m?: Message[] }> {
    const req =
      typeof idOrReq === 'string' ? { id: idOrReq, ...(opts ?? {}) } : idOrReq;
    const m: JsonObject = { id: req.id };
    if (req.read) m.read = 1;
    if (req.html) m.html = 1;
    if (req.raw) m.raw = 1;
    if (req.neuter !== undefined) m.neuter = req.neuter ? 1 : 0;
    if (req.part) m.part = req.part;
    if (req.max !== undefined) m.max = req.max;
    if (req.needExp) m.needExp = 1;
    if (req.ridZ) m.ridZ = req.ridZ;
    if (req.headers?.length) m.header = req.headers.map((n) => ({ n }));
    return this.client.call<{ m?: Message[] }>(JSNS.mail, 'GetMsg', { m });
  }

  /** GetConvRequest — fetch a conversation envelope plus its messages. */
  async getConversation(req: {
    id: string;
    fetch?: string;
    html?: boolean;
    max?: number;
    needExp?: boolean;
    headers?: string[];
  }): Promise<{ c?: Conversation[] }> {
    const c: JsonObject = { id: req.id };
    if (req.fetch) c.fetch = req.fetch;
    if (req.html) c.html = 1;
    if (req.max !== undefined) c.max = req.max;
    if (req.needExp) c.needExp = 1;
    if (req.headers?.length) c.header = req.headers.map((n) => ({ n }));
    return this.client.call<{ c?: Conversation[] }>(JSNS.mail, 'GetConv', { c });
  }

  /** GetMsgMetadataRequest — metadata (no bodies) for a set of message ids. */
  async getMsgMetadata(ids: string[]): Promise<{ m?: Message[] }> {
    return this.client.call<{ m?: Message[] }>(JSNS.mail, 'GetMsgMetadata', {
      m: { ids: ids.join(',') },
    });
  }

  /**
   * GetItemRequest — fetch any item by id or by folder-path+name/path without
   * knowing its type first. Returns the typed item element (`m`/`cn`/`folder`/…).
   */
  async getItem(ref: { id: string } | { path: string }): Promise<JsonObject> {
    const item: JsonObject =
      'id' in ref ? { id: ref.id } : { path: ref.path };
    return this.client.call(JSNS.mail, 'GetItem', { item });
  }

  /* ============================================================== */
  /* Item actions                                                    */
  /* ============================================================== */

  /**
   * MsgActionRequest — apply an op to one or more messages.
   * ops include: read, !read, flag, !flag, trash, delete, move, spam, !spam,
   * tag, !tag, update, mute … (see docs/features/mail.md).
   */
  async messageAction(
    ids: string | string[],
    op: string,
    opts: MessageActionOptions = {},
  ): Promise<JsonObject> {
    const { folderId, ...rest } = opts;
    const action: JsonObject = {
      id: joinIds(ids),
      op,
      ...rest,
    };
    if (folderId) action.l = folderId;
    return this.client.call(JSNS.mail, 'MsgAction', { action });
  }

  /** MsgActionRequest — typed variant. Workhorse for message toolbar/menu ops. */
  async msgAction(req: {
    ids: string | string[];
    op: MsgOp;
    folderId?: string;
    tagNames?: string[];
    flags?: string;
    color?: number;
    rgb?: string;
    name?: string;
    constraints?: string;
  }): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'MsgAction', {
      action: this.buildActionNode(req),
    });
  }

  /** ConvActionRequest — same op set as MsgAction, applied to conversations. */
  async convAction(req: {
    ids: string | string[];
    op: MsgOp;
    folderId?: string;
    tagNames?: string[];
    flags?: string;
    color?: number;
    rgb?: string;
    name?: string;
    constraints?: string;
  }): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'ConvAction', {
      action: this.buildActionNode(req),
    });
  }

  /** ItemActionRequest — type-agnostic action on arbitrary item ids. */
  async itemAction(req: {
    ids: string | string[];
    op: string;
    folderId?: string;
    tagNames?: string[];
    flags?: string;
    color?: number;
    rgb?: string;
    name?: string;
    constraints?: string;
  }): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'ItemAction', {
      action: this.buildActionNode(req),
    });
  }

  /** Build the shared `action` node for Msg/Conv/Item action requests. */
  private buildActionNode(req: {
    ids: string | string[];
    op: string;
    folderId?: string;
    tagNames?: string[];
    flags?: string;
    color?: number;
    rgb?: string;
    name?: string;
    constraints?: string;
  }): JsonObject {
    const action: JsonObject = { id: joinIds(req.ids), op: req.op };
    if (req.folderId) action.l = req.folderId;
    if (req.tagNames?.length) action.tn = req.tagNames.join(',');
    if (req.flags !== undefined) action.f = req.flags;
    if (req.color !== undefined) action.color = req.color;
    if (req.rgb) action.rgb = req.rgb;
    if (req.name !== undefined) action.name = req.name;
    if (req.constraints) action.tcon = req.constraints;
    return action;
  }

  /* ============================================================== */
  /* Folders                                                         */
  /* ============================================================== */

  /** GetFolderRequest — fetch the folder tree (or a sub-tree). */
  async getFolderTree(opts?: {
    folderId?: string;
    visible?: boolean;
    depth?: number;
  }): Promise<{ folder?: Folder[] }> {
    const params: JsonObject = {};
    if (opts?.folderId) params.folder = { l: opts.folderId };
    if (opts?.visible) params.visible = 1;
    if (opts?.depth !== undefined) params.depth = opts.depth;
    return this.client.call<{ folder?: Folder[] }>(JSNS.mail, 'GetFolder', params);
  }

  /** GetFolderRequest — full-featured variant (subtree by id/path, view, depth). */
  async getFolder(req?: {
    root?: { id: string } | { path: string };
    view?: FolderView;
    depth?: number;
    visible?: boolean;
    needGranteeName?: boolean;
    traverseMountpoints?: boolean;
  }): Promise<{ folder?: Folder[] }> {
    const params: JsonObject = {};
    if (req?.root) {
      params.folder = 'id' in req.root ? { l: req.root.id } : { path: req.root.path };
    }
    if (req?.view) params.view = req.view;
    if (req?.depth !== undefined) params.depth = req.depth;
    if (req?.visible) params.visible = 1;
    if (req?.needGranteeName) params.needGranteeName = 1;
    if (req?.traverseMountpoints) params.tr = 1;
    return this.client.call<{ folder?: Folder[] }>(JSNS.mail, 'GetFolder', params);
  }

  /** CreateFolderRequest — create a mail folder (optionally an RSS/feed folder). */
  async createFolder(req: {
    name: string;
    parentFolderId?: string;
    view?: FolderView;
    color?: number;
    rgb?: string;
    flags?: string;
    url?: string;
    fetchIfExists?: boolean;
    sync?: boolean;
  }): Promise<{ folder?: Folder[] }> {
    const folder: JsonObject = { name: req.name };
    if (req.parentFolderId) folder.l = req.parentFolderId;
    if (req.view) folder.view = req.view;
    if (req.color !== undefined) folder.color = req.color;
    if (req.rgb) folder.rgb = req.rgb;
    if (req.flags) folder.f = req.flags;
    if (req.url) folder.url = req.url;
    if (req.fetchIfExists) folder.fie = 1;
    if (req.sync !== undefined) folder.sync = req.sync ? 1 : 0;
    return this.client.call<{ folder?: Folder[] }>(JSNS.mail, 'CreateFolder', {
      folder,
    });
  }

  /**
   * FolderActionRequest — rename / move / delete / empty / mark-read / color /
   * grant / revoke / url / retentionpolicy / etc.
   *
   * Grant/revoke payload semantics are documented by the Sharing agent; the mail
   * folder tree invokes them here.
   */
  async folderAction(req: {
    id: string;
    op: FolderOp;
    folderId?: string;
    name?: string;
    color?: number;
    rgb?: string;
    flags?: string;
    recursive?: boolean;
    grant?: Grant;
    revokeGranteeId?: string;
    url?: string;
    retentionPolicy?: RetentionPolicy;
  }): Promise<JsonObject> {
    const action: JsonObject = { id: req.id, op: req.op };
    if (req.folderId) action.l = req.folderId;
    if (req.name !== undefined) action.name = req.name;
    if (req.color !== undefined) action.color = req.color;
    if (req.rgb) action.rgb = req.rgb;
    if (req.flags !== undefined) action.f = req.flags;
    if (req.recursive !== undefined) action.recursive = req.recursive ? 1 : 0;
    if (req.grant) action.grant = req.grant;
    if (req.revokeGranteeId) action.zid = req.revokeGranteeId;
    if (req.url) action.url = req.url;
    if (req.retentionPolicy) action.retentionPolicy = req.retentionPolicy;
    return this.client.call(JSNS.mail, 'FolderAction', { action });
  }

  /* ============================================================== */
  /* Tags                                                            */
  /* ============================================================== */

  /** GetTagRequest — the flat tag list. */
  async getTags(): Promise<{ tag?: Tag[] }> {
    return this.client.call<{ tag?: Tag[] }>(JSNS.mail, 'GetTag', {});
  }

  /** CreateTagRequest. */
  async createTag(req: {
    name: string;
    color?: number;
    rgb?: string;
  }): Promise<{ tag?: Tag[] }> {
    const tag: JsonObject = { name: req.name };
    if (req.color !== undefined) tag.color = req.color;
    if (req.rgb) tag.rgb = req.rgb;
    return this.client.call<{ tag?: Tag[] }>(JSNS.mail, 'CreateTag', { tag });
  }

  /** TagActionRequest — rename / color / delete / read on a tag definition. */
  async tagAction(req: {
    id: string;
    op: 'rename' | 'color' | 'delete' | 'read' | 'update';
    name?: string;
    color?: number;
    rgb?: string;
  }): Promise<JsonObject> {
    const action: JsonObject = { id: req.id, op: req.op };
    if (req.name !== undefined) action.name = req.name;
    if (req.color !== undefined) action.color = req.color;
    if (req.rgb) action.rgb = req.rgb;
    return this.client.call(JSNS.mail, 'TagAction', { action });
  }

  /* ============================================================== */
  /* Search folders (saved searches)                                 */
  /* ============================================================== */

  /** GetSearchFolderRequest. */
  async getSearchFolders(): Promise<{ search?: SearchFolder[] }> {
    return this.client.call<{ search?: SearchFolder[] }>(
      JSNS.mail,
      'GetSearchFolder',
      {},
    );
  }

  /** CreateSearchFolderRequest. */
  async createSearchFolder(req: {
    name: string;
    query: string;
    types?: SearchType[];
    sortBy?: SortBy | string;
    parentFolderId?: string;
  }): Promise<{ search?: SearchFolder[] }> {
    const search: JsonObject = { name: req.name, query: req.query };
    if (req.types?.length) search.types = req.types.join(',');
    if (req.sortBy) search.sortBy = req.sortBy;
    if (req.parentFolderId) search.l = req.parentFolderId;
    return this.client.call<{ search?: SearchFolder[] }>(
      JSNS.mail,
      'CreateSearchFolder',
      { search },
    );
  }

  /** ModifySearchFolderRequest. */
  async modifySearchFolder(req: {
    id: string;
    query: string;
    types?: SearchType[];
    sortBy?: SortBy | string;
  }): Promise<{ search?: SearchFolder[] }> {
    const search: JsonObject = { id: req.id, query: req.query };
    if (req.types?.length) search.types = req.types.join(',');
    if (req.sortBy) search.sortBy = req.sortBy;
    return this.client.call<{ search?: SearchFolder[] }>(
      JSNS.mail,
      'ModifySearchFolder',
      { search },
    );
  }

  /* ============================================================== */
  /* Spell check                                                     */
  /* ============================================================== */

  /** CheckSpellingRequest — returns misspelled words + suggestions. */
  async checkSpelling(req: {
    text: string;
    dictionary?: string;
    ignore?: string[];
  }): Promise<SpellCheckResult> {
    const body: JsonObject = { _content: req.text };
    if (req.dictionary) body.dictionary = req.dictionary;
    if (req.ignore?.length) body.ignore = req.ignore.join(',');
    const raw = await this.client.call<{
      available?: boolean | number;
      misspelled?: Array<{ word?: string; suggestions?: string }>;
    }>(JSNS.mail, 'CheckSpelling', body);
    const misspelled: MisspelledWord[] = (raw.misspelled ?? []).map((w) => ({
      word: w.word ?? '',
      suggestions: csvToList(w.suggestions),
    }));
    return { available: asBool(raw.available), misspelled };
  }

  /** GetSpellDictionariesRequest — installed dictionaries for the language picker. */
  async getSpellDictionaries(): Promise<string[]> {
    const raw = await this.client.call<{
      dictionary?: Array<string | { _content?: string }>;
    }>(JSNS.mail, 'GetSpellDictionaries', {});
    return (raw.dictionary ?? []).map((d) =>
      typeof d === 'string' ? d : (d._content ?? ''),
    );
  }

  /* ============================================================== */
  /* Autocomplete                                                    */
  /* ============================================================== */

  /** AutoCompleteRequest — as-you-type recipient suggestions. */
  async autoComplete(req: {
    name: string;
    type?: 'all' | 'account' | 'resource' | 'group';
    needExp?: boolean;
    folders?: string[];
    includeGal?: boolean;
  }): Promise<AutoCompleteMatch[]> {
    const body: JsonObject = { name: req.name };
    if (req.type) body.t = req.type;
    if (req.needExp) body.needExp = 1;
    if (req.folders?.length) body.folders = req.folders.join(',');
    if (req.includeGal !== undefined) body.includeGal = req.includeGal ? 1 : 0;
    const raw = await this.client.call<{ match?: AutoCompleteMatch[] }>(
      JSNS.mail,
      'AutoComplete',
      body,
    );
    return raw.match ?? [];
  }

  /** RankingActionRequest — maintain the "frequently emailed" ranking table. */
  async rankingAction(
    req: { op: 'reset' } | { op: 'delete'; email: string },
  ): Promise<JsonObject> {
    const action: JsonObject = { op: req.op };
    if (req.op === 'delete') action.email = req.email;
    return this.client.call(JSNS.mail, 'RankingAction', { action });
  }

  /* ============================================================== */
  /* Read receipts / dumpster                                        */
  /* ============================================================== */

  /** SendDeliveryReportRequest — send the MDN (read receipt) for a message. */
  async sendDeliveryReport(messageId: string): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'SendDeliveryReport', { mid: messageId });
  }

  /** EmptyDumpsterRequest — permanently purge the recover-deleted store. */
  async emptyDumpster(): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'EmptyDumpster', {});
  }

  /** ResetRecentMessageCountRequest — reset the mailbox "recent" counter. */
  async resetRecentMessageCount(): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'ResetRecentMessageCount', {});
  }

  /* ============================================================== */
  /* Mailbox metadata (per-mailbox UI state)                         */
  /* ============================================================== */

  /** GetMailboxMetadataRequest — read a metadata section (or the default). */
  async getMailboxMetadata(section?: string): Promise<MailboxMetadata> {
    const params: JsonObject = {};
    if (section) params.meta = { section };
    const raw = await this.client.call<{
      meta?: MailboxMetadata | MailboxMetadata[];
    }>(JSNS.mail, 'GetMailboxMetadata', params);
    const meta = Array.isArray(raw.meta) ? raw.meta[0] : raw.meta;
    return meta ?? {};
  }

  /** SetMailboxMetadataRequest — replace a metadata section. */
  async setMailboxMetadata(
    section: string,
    attrs: Record<string, MetadataValue>,
  ): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'SetMailboxMetadata', {
      meta: { section, _attrs: attrs },
    });
  }

  /** ModifyMailboxMetadataRequest — merge keys into a metadata section. */
  async modifyMailboxMetadata(
    section: string,
    attrs: Record<string, MetadataValue>,
  ): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'ModifyMailboxMetadata', {
      meta: { section, _attrs: attrs },
    });
  }

  /* ============================================================== */
  /* Custom (per-item) metadata                                      */
  /* ============================================================== */

  /** GetCustomMetadataRequest — arbitrary per-item key/value section. */
  async getCustomMetadata(id: string, section: string): Promise<MailboxMetadata> {
    const raw = await this.client.call<{
      meta?: MailboxMetadata | MailboxMetadata[];
    }>(JSNS.mail, 'GetCustomMetadata', { id, meta: { section } });
    const meta = Array.isArray(raw.meta) ? raw.meta[0] : raw.meta;
    return meta ?? {};
  }

  /** SetCustomMetadataRequest — set a per-item key/value section. */
  async setCustomMetadata(
    id: string,
    section: string,
    attrs: Record<string, MetadataValue>,
  ): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'SetCustomMetadata', {
      id,
      meta: { section, _attrs: attrs },
    });
  }

  /* ============================================================== */
  /* Browse facets                                                   */
  /* ============================================================== */

  /** BrowseRequest — aggregated facets (distinct domains/attachments/objects). */
  async browse(req: {
    browseBy: BrowseBy;
    regex?: string;
    max?: number;
  }): Promise<BrowseData[]> {
    const body: JsonObject = { browseBy: req.browseBy };
    if (req.regex) body.regex = req.regex;
    if (req.max !== undefined) body.maxToReturn = req.max;
    const raw = await this.client.call<{ bd?: BrowseData[] }>(
      JSNS.mail,
      'Browse',
      body,
    );
    return raw.bd ?? [];
  }

  /* ============================================================== */
  /* Notifications / sync (mostly internal heartbeats)               */
  /* ============================================================== */

  /**
   * NoOpRequest — poll for pending change notifications (the long-poll
   * heartbeat). The notification block rides back on the response context and is
   * absorbed by the client automatically.
   */
  async noop(opts?: {
    wait?: boolean;
    limitToOneBlocked?: boolean;
  }): Promise<JsonObject> {
    const body: JsonObject = {};
    if (opts?.wait) body.wait = 1;
    if (opts?.limitToOneBlocked) body.limitToOneBlocked = 1;
    return this.client.call(JSNS.mail, 'NoOp', body);
  }

  /** GetModifiedItemsIDsRequest — ids changed in a folder since a modSeq. */
  async getModifiedItemsIDs(req: {
    folderId: string;
    modSeq: number;
  }): Promise<string[]> {
    const raw = await this.client.call<{
      ids?: Array<string | { _content?: string }> | string;
    }>(JSNS.mail, 'GetModifiedItemsIDs', { l: req.folderId, ms: req.modSeq });
    if (typeof raw.ids === 'string') return csvToList(raw.ids);
    return (raw.ids ?? []).map((i) =>
      typeof i === 'string' ? i : (i._content ?? ''),
    );
  }

  /** SyncRequest — token-based delta sync (created/modified/deleted since token). */
  async sync(req?: {
    token?: string;
    folderId?: string;
    typed?: boolean;
  }): Promise<JsonObject> {
    const body: JsonObject = {};
    if (req?.token) body.token = req.token;
    if (req?.folderId) body.l = req.folderId;
    if (req?.typed !== undefined) body.typed = req.typed ? 1 : 0;
    return this.client.call(JSNS.mail, 'Sync', body);
  }

  /** CreateWaitSetRequest — server-push wait-set (lower-latency than NoOp poll). */
  async createWaitSet(req?: {
    defTypes?: string;
    allAccounts?: boolean;
  }): Promise<JsonObject> {
    const body: JsonObject = { defTypes: req?.defTypes ?? 'all' };
    if (req?.allAccounts) body.allAccounts = 1;
    return this.client.call(JSNS.mail, 'CreateWaitSet', body);
  }

  /** WaitSetRequest — block until the wait-set reports changes (or timeout). */
  async waitSet(req: {
    waitSet: string;
    seq: string;
    block?: boolean;
    timeout?: number;
  }): Promise<JsonObject> {
    const body: JsonObject = { waitSet: req.waitSet, seq: req.seq };
    if (req.block !== undefined) body.block = req.block ? 1 : 0;
    if (req.timeout !== undefined) body.timeout = req.timeout;
    return this.client.call(JSNS.mail, 'WaitSet', body);
  }

  /** DestroyWaitSetRequest — tear down a wait-set. */
  async destroyWaitSet(waitSet: string): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'DestroyWaitSet', { waitSet });
  }

  /** GetNotificationsRequest — pull the pending notify block explicitly. */
  async getNotifications(opts?: { markSeen?: boolean }): Promise<JsonObject> {
    const body: JsonObject = {};
    if (opts?.markSeen !== undefined) body.markSeen = opts.markSeen ? 1 : 0;
    return this.client.call(JSNS.mail, 'GetNotifications', body);
  }

  /* ============================================================== */
  /* Attachment servlet helpers (NOT SOAP — routed via BFF)          */
  /* ============================================================== */

  /**
   * Build the upload-servlet URL for a file attachment. Uploading is NOT a SOAP
   * command — it is a multipart POST to /service/upload that returns an `aid`
   * the compose methods consume. The actual HTTP is performed by the BFF.
   *
   * BFF endpoint, see docs/03-api.md — this helper only builds the URL pieces.
   */
  buildUploadUrl(opts?: { fmt?: 'extended' | 'raw' }): string {
    return `/service/upload?fmt=${opts?.fmt ?? 'extended'}`;
  }

  /**
   * Build a content-download URL for an attachment / inline part. Download is
   * servlet-based, not SOAP; the BFF attaches auth.
   *
   * BFF endpoint, see docs/03-api.md.
   */
  buildAttachmentUrl(req: { messageId: string; part: string; disposition?: 'inline' | 'attachment' }): string {
    const disp = req.disposition ? `&disp=${req.disposition}` : '';
    return `/service/content/get?id=${encodeURIComponent(req.messageId)}&part=${encodeURIComponent(req.part)}${disp}`;
  }

  /**
   * Perform an attachment upload. Intentionally NOT implemented in the SDK: the
   * upload is a multipart servlet POST handled by the BFF (see docs/03-api.md
   * and mail.md open question 1). Call the BFF endpoint, then pass the returned
   * upload id to compose methods as `{ uploadId }`.
   */
  async uploadAttachment(): Promise<never> {
    throw new Error('routed via BFF');
  }
}
